<template>
  <form
    id="payment-form"
    @submit.prevent="handleSubmit()"
    style="margin-bottom: 24px"
    v-if="clientSecret"
  >
    <div id="link-authentication-element" />
    <div id="payment-element" />
    <div id="messages" role="alert">{{ message }}</div>
    <f7-button
      class="margin-top payButton buttons no-margin-horizontal"
      v-if="payElement"
      @click="handleSubmit()"
      round-ios
      fill
      large
      ripple
      id="submit"
      :disabled="isLoading"
      strong
      color="red"
    >
      <div>
        <f7-preloader color="multi" v-if="isLoading" />
        <span>{{ $t("checkout.payNow") }}</span>
      </div>
    </f7-button>
  </form>
</template>

<style scoped>
@media screen and (max-width: 568px) {
  #payment-element,
  #link-authentication-element,
  #messages {
    margin-left: 8px;
    margin-right: 8px;
  }
}
</style>

<script setup>
import { f7, theme } from "framework7-vue";
import { loadStripe } from "@stripe/stripe-js";
import { ref, computed, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useApimoneyStore } from "../js/pinia-stores/apimoney.js";
import { useStripeStore } from "../js/pinia-stores/stripe.js";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import { useTabletScreen } from "../js/composables/useMediaQuery.js";
import { useAccountStore } from "../js/pinia-stores/account.js";
import _ from "lodash";

const props = defineProps({
  f7route: Object,
  f7router: Object,
  stripeAccountId: String,
  clientSecret: String,
});

const emit = defineEmits(['payment-success', 'payment-error']);

const apimoneyStore = useApimoneyStore();
const stripeStore = useStripeStore();
const authStore = useAuthStore();
const accountStore = useAccountStore();

const { publishableKey } = storeToRefs(stripeStore);
const { profile, documentsUrl, document, isNativePlatform } = storeToRefs(authStore);
const isTablet = useTabletScreen().matches;
const { userAccount, userDetail, searchedUser } = storeToRefs(accountStore);

const elements = ref(null);
const isLoading = ref(false);
const message = ref("");
const payElement = ref(null);
const stripeElement = ref(null);

async function initializeStripe() {
  if (!publishableKey.value) {
    await stripeStore.getStripePublicKey();
  }
  
  if (!props.clientSecret) return;
  
  stripeElement.value = await loadStripe(publishableKey.value, {
    stripeAccount: props.stripeAccountId,
  });

  if (stripeElement.value && props.clientSecret) {
    elements.value = stripeElement.value.elements({ clientSecret: props.clientSecret });
    const paymentElement = elements.value.create("payment", {
      layout: {
        type: isTablet.value ? "tabs" : "accordion",
      },
      defaultValues: {
        billingDetails: {
          name: userAccount.value?.name || '',
          email: userAccount.value?.standard?.standard_info?.email || '',
          phone: userAccount.value?.username || '',
          address: {
            line1: `${userAccount.value?.standard?.address?.label1 || ''} ${userDetail.value?.standard?.address?.label2 || ''}`,
            postal_code: userAccount.value?.standard?.address?.zip_code || '',
            city: userAccount.value?.standard?.address?.city || '',
            state: userAccount.value?.standard?.address?.label3 || '',
            country: userAccount.value?.countryCode || '',
          },
        },
      },
    });
    paymentElement.mount("#payment-element");
    payElement.value = paymentElement;
    const linkAuthenticationElement = elements.value.create("linkAuthentication");
    linkAuthenticationElement.mount("#link-authentication-element");
  }
}

async function handleSubmit() {
  if (isLoading.value || !payElement.value || !stripeElement.value) return;
  
  isLoading.value = true;
  message.value = "";

  try {
    const { error } = await stripeElement.value.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: isNativePlatform.value
          ? `${import.meta.env.VITE_AVOCASH_SITE_URL}/transaction/success`
          : `${window.location.origin}/transaction/success`,
      },
    });
    
    if (error) {
      message.value = error.type === "card_error" || error.type === "validation_error"
        ? error.message
        : "An unexpected error occurred.";
      emit('payment-error', error);
    } else {
      emit('payment-success', { clientSecret: props.clientSecret });
    }
  } catch (error) {
    message.value = error.message || "An unexpected error occurred.";
    emit('payment-error', error);
  } finally {
    isLoading.value = false;
  }
}

watch(() => props.clientSecret, (newVal) => {
  if (newVal) {
    initializeStripe();
  }
}, { immediate: true });

watch(() => publishableKey.value, (newVal) => {
  if (newVal && props.clientSecret) {
    initializeStripe();
  }
});

onMounted(() => {
  if (!publishableKey.value) {
    stripeStore.getStripePublicKey();
  }
});
</script>
